import React, { useCallback, useEffect, useState } from 'react';
import { BarChart3, ChevronDown, ChevronRight, Download, RefreshCw } from 'lucide-react';
import { reportsService } from '../services/reports.service';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { formatMoney } from '../utils/formatMoney';
import { downloadCsv, flattenMonthVendorRows } from '../utils/csvExport';

// Month-wise revenue / commission / GST from paid customer invoices, with a
// per-vendor breakdown. Commission = the platform fee charged to the customer.
// Revenue is GST-exclusive and includes the commission; vendor earnings =
// revenue - commission.

const RANGE_OPTS = [
    { label: 'Last 6 months', months: 6 },
    { label: 'Last 12 months', months: 12 },
    { label: 'Last 24 months', months: 24 },
];

const Num = ({ v }) => <span className="tabular-nums">{formatMoney(v)}</span>;

const REVENUE_CSV_HEADERS = [
    'Month',
    'Level',
    'Vendor',
    'Jobs',
    'Revenue (ex GST)',
    'Commission',
    'Vendor earnings',
    'GST',
    'Total',
];

function revenueCsvRows(data) {
    return flattenMonthVendorRows(
        data?.months,
        (month, t) => [
            month,
            'Month total',
            '',
            t.jobs ?? 0,
            t.revenue ?? '',
            t.commission ?? '',
            t.vendor_earnings ?? '',
            t.gst ?? '',
            t.total ?? '',
        ],
        (month, v) => [
            month,
            'Vendor',
            v.vendor_name || '',
            v.jobs ?? 0,
            v.revenue ?? '',
            v.commission ?? '',
            v.vendor_earnings ?? '',
            v.gst ?? '',
            v.total ?? '',
        ],
    );
}

const RevenueReport = () => {
    const [months, setMonths] = useState(12);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [open, setOpen] = useState({});

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        reportsService
            .getRevenueReport({ months })
            .then((res) => {
                setData(res);
                // Expand the most recent month by default.
                const first = res.months?.[0]?.month;
                setOpen(first ? { [first]: true } : {});
            })
            .catch((e) => setError(e?.message || 'Failed to load report'))
            .finally(() => setLoading(false));
    }, [months]);

    useEffect(() => { load(); }, [load]);

    const handleExport = () => {
        if (!data?.months?.length) return;
        downloadCsv(
            `revenue-report-last-${months}-months.csv`,
            REVENUE_CSV_HEADERS,
            revenueCsvRows(data),
        );
    };

    const g = data?.grand_totals;
    const canExport = !loading && !!data?.months?.length;

    return (
        <div className="p-6">
            <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-6 h-6 text-gray-700" />
                <h1 className="text-xl font-semibold">Revenue Report</h1>
            </div>
            <p className="text-sm text-gray-500 mb-4">
                Month-wise revenue, commission and GST from paid customer invoices,
                broken down by vendor.
            </p>

            <div className="flex items-center gap-3 mb-4">
                <select
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                    className="border rounded-lg px-3 py-2 text-sm">
                    {RANGE_OPTS.map((o) => (
                        <option key={o.months} value={o.months}>{o.label}</option>
                    ))}
                </select>
                <button
                    onClick={load}
                    className="inline-flex items-center gap-1 text-sm px-3 py-2 border rounded-lg hover:bg-gray-50">
                    <RefreshCw className="w-4 h-4" /> Refresh
                </button>
                <button
                    type="button"
                    onClick={handleExport}
                    disabled={!canExport}
                    className="inline-flex items-center gap-1 text-sm px-3 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                    <Download className="w-4 h-4" /> Export CSV
                </button>
                {data?.currency && (
                    <span className="text-xs text-gray-400 ml-auto">Amounts in {data.currency}</span>
                )}
            </div>

            {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

            {loading ? (
                <LoadingSpinner />
            ) : !data || !data.months?.length ? (
                <div className="text-sm text-gray-400 border rounded-xl p-8 text-center">
                    No paid invoices in this period.
                </div>
            ) : (
                <>
                    {/* Grand totals */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                        {[
                            ['Jobs', g.jobs, true],
                            ['Revenue (ex GST)', g.revenue],
                            ['Commission', g.commission],
                            ['Vendor earnings', g.vendor_earnings],
                            ['GST', g.gst],
                        ].map(([label, val, isCount]) => (
                            <div key={label} className="border rounded-xl px-4 py-3">
                                <div className="text-xs text-gray-500">{label}</div>
                                <div className="text-lg font-semibold tabular-nums">
                                    {isCount ? val : formatMoney(val)}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="overflow-x-auto border rounded-xl">
                        <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 text-gray-500">
                                <tr>
                                    <th className="text-left px-3 py-2">Month / Vendor</th>
                                    <th className="text-right px-3 py-2">Jobs</th>
                                    <th className="text-right px-3 py-2">Revenue (ex GST)</th>
                                    <th className="text-right px-3 py-2">Commission</th>
                                    <th className="text-right px-3 py-2">Vendor earnings</th>
                                    <th className="text-right px-3 py-2">GST</th>
                                    <th className="text-right px-3 py-2">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.months.map((m) => {
                                    const isOpen = !!open[m.month];
                                    return (
                                        <React.Fragment key={m.month}>
                                            <tr
                                                className="border-t bg-gray-50/60 font-medium cursor-pointer hover:bg-gray-100"
                                                onClick={() => setOpen((o) => ({ ...o, [m.month]: !o[m.month] }))}>
                                                <td className="px-3 py-2 flex items-center gap-1">
                                                    {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                                    {m.month}
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums">{m.totals.jobs}</td>
                                                <td className="px-3 py-2 text-right"><Num v={m.totals.revenue} /></td>
                                                <td className="px-3 py-2 text-right"><Num v={m.totals.commission} /></td>
                                                <td className="px-3 py-2 text-right"><Num v={m.totals.vendor_earnings} /></td>
                                                <td className="px-3 py-2 text-right"><Num v={m.totals.gst} /></td>
                                                <td className="px-3 py-2 text-right"><Num v={m.totals.total} /></td>
                                            </tr>
                                            {isOpen && m.vendors.map((v) => (
                                                <tr key={`${m.month}-${v.vendor_id ?? 'none'}`} className="border-t">
                                                    <td className="px-3 py-2 pl-9 text-gray-600">{v.vendor_name}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{v.jobs}</td>
                                                    <td className="px-3 py-2 text-right"><Num v={v.revenue} /></td>
                                                    <td className="px-3 py-2 text-right"><Num v={v.commission} /></td>
                                                    <td className="px-3 py-2 text-right"><Num v={v.vendor_earnings} /></td>
                                                    <td className="px-3 py-2 text-right"><Num v={v.gst} /></td>
                                                    <td className="px-3 py-2 text-right"><Num v={v.total} /></td>
                                                </tr>
                                            ))}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default RevenueReport;
